import { useState, useEffect } from "react";
import { H1Title } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import NotificationService from "../services/notificationService";
import { Error as ToastError } from "../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCheckDouble, faCircle } from "@fortawesome/free-solid-svg-icons";

const TYPE_COLORS = {
  round_advance: 'text-green',
  round_eliminate: 'text-red-500',
  submission_received: 'text-primary',
  score_available: 'text-orange-500',
  fundraising_update: 'text-green',
  milestone_due: 'text-orange-500',
  judge_assignment: 'text-primary',
  team_invite: 'text-primary',
  general: 'text-grey-667',
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      const { data } = await NotificationService.getMyNotifications({ limit: 50 });
      setNotifications(data.data.notifications || []);
      setUnreadCount(data.data.unreadCount || 0);
    } catch (err) {
      ToastError('Failed to load notifications');
    } finally { setLoading(false); }
  };

  const handleMarkRead = async (id) => {
    try {
      await NotificationService.markRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await NotificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const t = new Date(date).getTime();
    if (Number.isNaN(t)) return '';
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const formatType = (type) => {
    if (!type || typeof type !== 'string') return 'general';
    return type.replace(/_/g, ' ');
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <H1Title size="sm">Notifications</H1Title>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <AppButton onClick={handleMarkAllRead} variant="outline" size="sm" fullWidth={false} className="px-4">
            <FontAwesomeIcon icon={faCheckDouble} className="mr-1" /> Mark All Read
          </AppButton>
        )}
      </div>

      <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-grey-667">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center text-grey-667">
            <FontAwesomeIcon icon={faBell} className="text-3xl text-grey-200 mb-3" />
            <p>No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-grey-200">
            {notifications.map((n) => (
              <div
                key={n._id}
                onClick={() => !n.isRead && handleMarkRead(n._id)}
                className={`px-5 py-4 cursor-pointer transition-colors ${n.isRead ? '' : 'bg-primary-bg/30'} hover:bg-grey-bg/50`}
              >
                <div className="flex items-start gap-3">
                  {!n.isRead && (
                    <FontAwesomeIcon icon={faCircle} className="text-primary text-[6px] mt-2 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-semibold ${n.isRead ? 'text-grey-dark' : 'text-black'}`}>{n.title}</p>
                      <span className="text-xs text-grey-98 flex-shrink-0 ml-2">{timeAgo(n.createdAt)}</span>
                    </div>
                    <p className="text-sm text-grey-667 mt-0.5">{n.body}</p>
                    <span className={`text-xs mt-1 inline-block ${TYPE_COLORS[n.type] || TYPE_COLORS.general}`}>
                      {formatType(n.type)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
